import React, { useMemo } from "react";
import {
  Cpu,
  Store,
  CalendarDays,
  Activity,
} from "lucide-react";

import TopBar from "../TopBar/TopBar";
import ShopColumn from "../common/TableComponents/ShopColumn.jsx";
import ShopTrendsChart from "../charts/ShopTrendsChart";

import { useDashBoardData } from "../../services/API/GetDashBoardDataAPI";

/**
 * Reusable dashboard metric card.
 *
 * Displays a title, a numeric value and an optional Lucide icon.
 * The styling matches the metric cards used in the e-commerce dashboard.
 */
const DashboardCard = ({
  title,
  desc,
  icon: Icon,
}) => {
  return (
    <div className="rounded-xl bg-white p-5 shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="mt-2 text-3xl font-bold">{desc}</h3>
        </div>

        {Icon && (
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-stone-100">
            <Icon className="h-6 w-6 text-[#222731]" />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Main controller and shop dashboard page.
 */
const Dashboard = ({ user }) => {
  /**
   * Fetches dashboard information from the backend.
   */
  const {
    data,
    isPending,
    error,
  } = useDashBoardData(user);

  /**
   * Extracts the dashboard payload returned by the API.
   */
  const filteredData = useMemo(() => {
    return data?.data ?? null;
  }, [data]);

  /**
   * Stores the complete shop list.
   *
   * Returns an empty array when the API does not provide shops.
   */
  const shops = useMemo(() => {
    return filteredData?.shops ?? [];
  }, [filteredData]);

  /**
   * Sorts shops by creation date.
   *
   * The newest shop is displayed first.
   */
  const latestShops = useMemo(() => {
    return [...shops].sort((shopA, shopB) => {
      const firstDate = new Date(
        shopA.date_created,
      ).getTime();

      const secondDate = new Date(
        shopB.date_created,
      ).getTime();

      return secondDate - firstDate;
    });
  }, [shops]);

  /**
   * Counts shops created during the current month and year.
   */
  const currentMonthShops = useMemo(() => {
    const today = new Date();

    return shops.filter((shop) => {
      const createdAt = new Date(
        shop.date_created,
      );

      /**
       * Ignores shops with invalid creation dates.
       */
      if (Number.isNaN(createdAt.getTime())) {
        return false;
      }

      return (
        createdAt.getMonth() ===
          today.getMonth() &&
        createdAt.getFullYear() ===
          today.getFullYear()
      );
    }).length;
  }, [shops]);

  /**
   * Counts shops created during the current year.
   */
  const currentYearShops = useMemo(() => {
    const currentYear =
      new Date().getFullYear();

    return shops.filter((shop) => {
      const createdAt = new Date(
        shop.date_created,
      );

      if (Number.isNaN(createdAt.getTime())) {
        return false;
      }

      return (
        createdAt.getFullYear() ===
        currentYear
      );
    }).length;
  }, [shops]);

  /**
   * Displays the dashboard loading state.
   */
  if (isPending) {
    return (
      <div className="flex h-80 items-center justify-center">
        <div className="rounded-xl bg-white px-6 py-4 text-stone-500 shadow">
          Loading dashboard...
        </div>
      </div>
    );
  }

  /**
   * Displays an error message when the API request fails.
   */
  if (error) {
    return (
      <div className="flex h-80 items-center justify-center">
        <div className="rounded-xl bg-white px-6 py-4 text-red-500 shadow">
          Error:{" "}
          {error.message ||
            "Unable to load dashboard."}
        </div>
      </div>
    );
  }

  /**
   * Displays an empty state when no dashboard payload is available.
   */
  if (!filteredData) {
    return (
      <div className="flex h-80 items-center justify-center">
        <div className="rounded-xl bg-white px-6 py-4 text-stone-500 shadow">
          No dashboard data available.
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden pb-2">
      
      {/* Dashboard summary cards. */}
      <main>
        <div className="grid auto-rows-fr grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">

          {/* Displays the number of controllers currently connected to the platform.*/}
          <DashboardCard
            title="Controllers Online"
            desc={
              filteredData.connectedDeviceCount ??
              0
            }
            icon={Cpu}
          />

          {/* Displays the total number of shops registered on the platform. */}
          <DashboardCard
            title="Total Shops Created"
            desc={
              filteredData.totalShops ??
              shops.length
            }
            icon={Store}
          />

          {/* Displays the number of shops created during the current month. */}
          <DashboardCard
            title="Shops Created This Month"
            desc={currentMonthShops}
            icon={CalendarDays}
          />

          {/* Displays the number of shops created during the current year. */}
          <DashboardCard
            title="Shops Created This Year"
            desc={currentYearShops}
            icon={Activity}
          />
        </div>

        {/* Main dashboard content.
         * The shop table and the trends chart have equal width on extra-large screens. */}
        <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">

          {/* Displays the shop list sorted from newest to oldest.*/}
          <section className="h-full">
            <ShopColumn
              shopList={latestShops}
            />
          </section>

          {/* Displays shop creation trends over time. */}
          <section className="h-full">
            <ShopTrendsChart
              shopList={shops}
            />
          </section>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;

